/*
 * TanStack Query hooks, one group per resource in docs/api.md.
 */
import { queryOptions, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrg } from "../app/org";
import { canSeeServers } from "../lib/roles";
import { api, qs } from "./client";
import { keys, type OpFilter } from "./keys";
import { bufferedLogs, mergeLogs, useLiveState, watchOperation } from "./live";
import {
  isActiveOp,
  type ActivityEvent,
  type AuthState,
  type CreateServer,
  type CreateServerResponse,
  type CreateSite,
  type CreateSiteResponse,
  type LinodeMeta,
  type LoginResponse,
  type Me,
  type Member,
  type MetricPoint,
  type MetricsRange,
  type Operation,
  type OperationWithLogs,
  type OrgInfo,
  type RestorePointRef,
  type ServerDetail,
  type ServerSummary,
  type SiteDetail,
  type SiteSummary,
  type TotpSetup,
} from "./types";

const o = (orgId: string) => `/orgs/${orgId}`;

/* ── Auth ───────────────────────────────────────────────────────── */

export const authStateQuery = () =>
  queryOptions({
    queryKey: keys.authState,
    queryFn: ({ signal }) => api.get<AuthState>("/auth/state", signal),
    staleTime: Infinity,
  });

export const meQuery = () =>
  queryOptions({
    queryKey: keys.me,
    queryFn: ({ signal }) => api.get<Me>("/me", signal),
    staleTime: 60_000,
  });

export const useMe = () => useQuery(meQuery());

function useSignedIn() {
  const qc = useQueryClient();
  return (me: Me) => {
    qc.setQueryData(keys.me, me);
    qc.setQueryData<AuthState>(keys.authState, { setupRequired: false, signedIn: true, mfaPending: false });
  };
}

export function useLogin() {
  const qc = useQueryClient();
  const signedIn = useSignedIn();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) => api.post<LoginResponse>("/auth/login", body),
    onSuccess: (res) => {
      if ("me" in res) signedIn(res.me);
      else qc.setQueryData<AuthState>(keys.authState, { setupRequired: false, signedIn: false, mfaPending: true });
    },
  });
}

export function useSetup() {
  const signedIn = useSignedIn();
  return useMutation({
    mutationFn: (body: { name: string; email: string; password: string; orgName: string }) =>
      api.post<{ me: Me }>("/auth/setup", body),
    onSuccess: (res) => signedIn(res.me),
  });
}

export function useMfa() {
  const signedIn = useSignedIn();
  return useMutation({
    mutationFn: (body: { code: string }) => api.post<{ me: Me; recoveryCodesLeft?: number }>("/auth/mfa", body), // recoveryCodesLeft: signed in with one
    onSuccess: (res) => signedIn(res.me),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>("/auth/logout"),
    onSettled: () => {
      qc.clear();
      qc.setQueryData<AuthState>(keys.authState, { setupRequired: false, signedIn: false, mfaPending: false });
    },
  });
}

export function useTotpSetup() {
  return useMutation({ mutationFn: () => api.post<TotpSetup>("/me/totp/setup") });
}

/** `issuer`: the name the authenticator app shows, for the recovery codes file. Then /me again (codes left, this session). */
export function useTotpEnable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string }) => api.post<{ recoveryCodes: string[]; issuer?: string }>("/me/totp/enable", body),
    onSuccess: () => {
      qc.setQueryData<Me>(keys.me, (me) => (me ? { ...me, user: { ...me.user, totpEnabled: true } } : me));
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/** With a code from the app, or a recovery code. */
export function useTotpDisable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { code: string }) => api.post<{ ok: true }>("/me/totp/disable", body),
    onSuccess: () => {
      qc.setQueryData<Me>(keys.me, (me) => (me ? { ...me, user: { ...me.user, totpEnabled: false } } : me));
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/* ── Servers ────────────────────────────────────────────────────── */

/**
 * `accounts`: every account's too (a platform admin on the platform org; app/accounts.tsx), each with its `account`.
 * Only asked for by people who see servers here (docs/plans.md); for everyone else there are none.
 */
export function useServers(opts: { accounts?: boolean } = {}) {
  const org = useOrg();
  const across = !!opts.accounts;
  return useQuery({
    queryKey: across ? ([...keys.servers(org.id), "accounts"] as const) : keys.servers(org.id),
    queryFn: ({ signal }) => api.get<{ servers: ServerSummary[] }>(`${o(org.id)}/servers${across ? "?accounts=all" : ""}`, signal),
    select: (d) => d.servers,
    enabled: canSeeServers(org),
    // Live events cover the org's own; the other accounts' are read again each minute.
    refetchInterval: across ? 60_000 : undefined,
  });
}

/** Several servers' details (the Overview reads the mail servers' queues and certificates). `orgIds`: each one's org, across accounts. */
export function useServerDetails(ids: string[], orgIds?: (string | null | undefined)[]) {
  const org = useOrg();
  return useQueries({
    queries: ids.map((id, i) => ({
      queryKey: keys.server(orgIds?.[i] ?? org.id, id),
      queryFn: ({ signal }: { signal: AbortSignal }) => api.get<{ server: ServerDetail }>(`${o(orgIds?.[i] ?? org.id)}/servers/${id}`, signal),
      select: (d: { server: ServerDetail }) => d.server,
      staleTime: 60_000,
    })),
  });
}

export function useServer(id: string | null, enabled = true) {
  const org = useOrg();
  return useQuery({
    queryKey: keys.server(org.id, id ?? ""),
    queryFn: ({ signal }) => api.get<{ server: ServerDetail }>(`${o(org.id)}/servers/${id}`, signal),
    select: (d) => d.server,
    enabled: enabled && !!id && canSeeServers(org), // no id: a server the person doesn't see
  });
}

export function useServerMetrics(id: string, range: MetricsRange, enabled = true) {
  const org = useOrg();
  return useQuery({
    queryKey: keys.metrics(org.id, id, range),
    queryFn: ({ signal }) =>
      api.get<{ points: MetricPoint[] }>(`${o(org.id)}/servers/${id}/metrics${qs({ range })}`, signal),
    select: (d) => d.points,
    enabled,
    staleTime: range === "1h" ? 60_000 : 5 * 60_000,
  });
}

export function useLinodeMeta(enabled = true) {
  return useQuery({
    queryKey: keys.linodeMeta,
    queryFn: ({ signal }) => api.get<LinodeMeta>("/meta/linode", signal),
    staleTime: 60 * 60_000,
    enabled,
  });
}

/** Region id → Linode's label ("us-east" → "Newark, NJ"); the id when unknown. */
export function useRegionLabel(enabled = true) {
  const meta = useLinodeMeta(enabled);
  return (id: string | null | undefined): string | null => {
    if (!id) return null;
    return meta.data?.regions.find((r) => r.id === id)?.label ?? id;
  };
}

export function useCreateServer() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateServer) => api.post<CreateServerResponse>(`${o(org.id)}/servers`, body),
    onSuccess: (res) => {
      qc.setQueryData<{ servers: ServerSummary[] }>(keys.servers(org.id), (old) =>
        old && !old.servers.some((s) => s.id === res.server.id) ? { servers: [...old.servers, res.server] } : old,
      );
      qc.invalidateQueries({ queryKey: keys.servers(org.id) });
    },
  });
}

export function useRefreshServer(id: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ operationId: string }>(`${o(org.id)}/servers/${id}/refresh`),
    onSuccess: (res) => {
      watchOperation(res.operationId);
      qc.invalidateQueries({ queryKey: keys.operationLists(org.id) });
    },
  });
}

export function useUpdateAgent(id: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ operationId: string }>(`${o(org.id)}/servers/${id}/agent/update`),
    onSuccess: (res) => {
      watchOperation(res.operationId);
      qc.invalidateQueries({ queryKey: keys.operationLists(org.id) });
    },
  });
}

/** "Set up again": server.bootstrap once more, for a server whose setup failed. */
export function useSetupAgain(id: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ operationId: string }>(`${o(org.id)}/servers/${id}/setup`, {}),
    onSuccess: (res) => {
      watchOperation(res.operationId);
      qc.invalidateQueries({ queryKey: keys.server(org.id, id) });
      qc.invalidateQueries({ queryKey: keys.operationLists(org.id) });
    },
  });
}

export function useDeleteServer(id: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    // A server can only be deleted once it has no environments, so nothing is backed up first.
    mutationFn: (confirmName: string) =>
      api.del<{ ok?: true; operationId?: string | null }>(`${o(org.id)}/servers/${id}`, { confirmName }),
    onSuccess: (res) => {
      if (res.operationId) watchOperation(res.operationId);
      qc.setQueryData<{ servers: ServerSummary[] }>(keys.servers(org.id), (old) =>
        old ? { servers: old.servers.filter((s) => s.id !== id) } : old,
      );
      qc.removeQueries({ queryKey: keys.server(org.id, id) });
      qc.invalidateQueries({ queryKey: keys.servers(org.id) });
    },
  });
}

/* ── Sites ──────────────────────────────────────────────────────── */

/**
 * The org's sites. `includeClients` (the Sites page of an agency or the platform) adds its client orgs' sites, each
 * with `client`; that list is kept under its own key, inside the org's sites key, so refreshing one refreshes both.
 * Client orgs' events come on their own streams, so that list also re-reads every minute.
 */
/** `accounts`: every account's sites (a platform admin on the platform org; app/accounts.tsx), each with its `account`. */
export function useSites(opts: { includeClients?: boolean; accounts?: boolean } = {}) {
  const org = useOrg();
  const across = !!opts.accounts;
  const withClients = !across && !!opts.includeClients;
  return useQuery({
    queryKey: across ? ([...keys.sites(org.id), "accounts"] as const) : withClients ? ([...keys.sites(org.id), "with-clients"] as const) : keys.sites(org.id),
    // With includeClients too, so an API without accounts=all still answers with the clients' sites.
    queryFn: ({ signal }) => api.get<{ sites: SiteSummary[] }>(`${o(org.id)}/sites${across ? "?accounts=all&includeClients=1" : withClients ? "?includeClients=1" : ""}`, signal),
    select: (d) => d.sites,
    // Live events cover the org's own (and its clients'); everything else is read again each minute.
    refetchInterval: across ? 60_000 : withClients ? (q) => ((q.state.data?.sites ?? []).some((s) => s.client) ? 60_000 : false) : undefined,
  });
}

export function useSite(id: string) {
  const org = useOrg();
  return useQuery({
    queryKey: keys.site(org.id, id),
    queryFn: ({ signal }) => api.get<{ site: SiteDetail }>(`${o(org.id)}/sites/${id}`, signal),
    select: (d) => d.site,
  });
}

export function useCreateSite() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSite) => api.post<CreateSiteResponse>(`${o(org.id)}/sites`, body),
    onSuccess: (res) => {
      watchOperation(res.operationId);
      qc.setQueryData<{ sites: SiteSummary[] }>(keys.sites(org.id), (old) =>
        old && !old.sites.some((s) => s.id === res.site.id) ? { sites: [...old.sites, res.site] } : old,
      );
      qc.invalidateQueries({ queryKey: keys.sites(org.id) });
      qc.invalidateQueries({ queryKey: keys.servers(org.id) });
    },
  });
}

export function useEnvAction(siteId: string, action: "apply" | "health") {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (envId: string) =>
      api.post<{ operationId: string }>(`${o(org.id)}/sites/${siteId}/environments/${envId}/${action}`),
    onSuccess: (res) => {
      watchOperation(res.operationId, undefined, { id: org.id, slug: org.slug });
      qc.invalidateQueries({ queryKey: keys.operationLists(org.id) });
    },
  });
}

export function useDeleteSite(id: string) {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    // Final backups of every environment run first; `restorePoint: false` is the explicit "continue without".
    mutationFn: (v: { confirmName: string; restorePoint?: boolean }) =>
      api.del<{
        operationId?: string;
        operationIds?: string[];
        restorePoint?: RestorePointRef;
        restorePoints?: { environmentId: string; backupId: string; operationId: string }[];
      }>(`${o(org.id)}/sites/${id}`, { confirmName: v.confirmName, ...(v.restorePoint === false ? { restorePoint: false } : {}) }),
    onSuccess: (res) => {
      if (res.operationId) watchOperation(res.operationId);
      res.operationIds?.forEach((id) => watchOperation(id));
      qc.invalidateQueries({ queryKey: keys.site(org.id, id) });
      qc.invalidateQueries({ queryKey: keys.sites(org.id) });
      qc.invalidateQueries({ queryKey: keys.servers(org.id) });
    },
  });
}

/* ── Operations and activity ────────────────────────────────────── */

export function useOperations(filter: OpFilter = {}, enabled = true) {
  const org = useOrg();
  const f: OpFilter = { limit: 50, ...filter };
  return useQuery({
    queryKey: keys.operations(org.id, f),
    enabled,
    queryFn: ({ signal }) => api.get<{ operations: Operation[] }>(`${o(org.id)}/operations${qs({ ...f, routine: f.routine ? 1 : undefined })}`, signal),
    // Newest first, whatever order the API used.
    select: (d) => [...d.operations].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)),
    // A fallback beside the live stream: while something in the list runs, or a restore point's work is about to
    // start, read the list again every few seconds, so a step never waits on an event that didn't come.
    refetchInterval: (q) => (q.state.data?.operations.some((op) => isActiveOp(op.status) || op.followUp?.status === "waiting") ? 5_000 : false),
  });
}

export function useOperation(id: string | null | undefined) {
  const org = useOrg();
  const live = useLiveState();
  return useQuery({
    queryKey: keys.operation(org.id, id ?? "none"),
    queryFn: async ({ signal }) => {
      const res = await api.get<OperationWithLogs>(`${o(org.id)}/operations/${id}`, signal);
      return { ...res, logs: mergeLogs(res.logs, bufferedLogs(res.operation.id)) };
    },
    enabled: !!id,
    staleTime: 3_000,
    // While it runs, read it again every few seconds (faster with the stream down), so a silent stream can't freeze it.
    refetchInterval: (q) => (q.state.data && (isActiveOp(q.state.data.operation.status) || q.state.data.operation.followUp?.status === "waiting") ? (live === "open" ? 4_000 : 3_000) : false),
  });
}

export function useActivity(limit = 50) {
  const org = useOrg();
  return useQuery({
    queryKey: keys.activity(org.id, limit),
    queryFn: ({ signal }) => api.get<{ events: ActivityEvent[] }>(`${o(org.id)}/activity${qs({ limit })}`, signal),
    select: (d) => d.events,
  });
}

/* ── Organization ───────────────────────────────────────────────── */

export function useOrgInfo() {
  const org = useOrg();
  return useQuery({
    queryKey: keys.orgInfo(org.id),
    queryFn: ({ signal }) => api.get<OrgInfo>(o(org.id), signal),
  });
}

export function useUpdateOrg() {
  const org = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name?: string; requireMfa?: boolean; timezone?: string }) => api.patch<{ org: OrgInfo["org"] }>(o(org.id), body),
    onSuccess: (res) => {
      qc.setQueryData<OrgInfo>(keys.orgInfo(org.id), (old) => (old ? { ...old, org: res.org } : old));
      // The org name also shows in the sidebar, which reads it from /me.
      qc.setQueryData<Me>(keys.me, (me) =>
        me ? { ...me, orgs: me.orgs.map((x) => (x.id === res.org.id ? { ...x, name: res.org.name } : x)) } : me,
      );
      qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useMembers() {
  const org = useOrg();
  return useQuery({
    queryKey: keys.members(org.id),
    queryFn: ({ signal }) => api.get<{ members: Member[] }>(`${o(org.id)}/members`, signal),
    select: (d) => d.members,
  });
}
